"use client";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PALETTE, AXIS_STYLE, TOOLTIP_STYLE } from "@/lib/recharts-theme";
import type { StartupRecord } from "@/lib/types";

const TOP_MRR = 50_000;
const MID_MRR = 10_000;

type Props = {
  startups: StartupRecord[];
};

type Segment = {
  label: string;
  count: number;
  threshold: string;
};

function segmentByMrr(startups: StartupRecord[]): Segment[] {
  const top = startups.filter((s) => s.mrr > TOP_MRR);
  const mid = startups.filter((s) => s.mrr >= MID_MRR && s.mrr <= TOP_MRR);
  const early = startups.filter((s) => s.mrr < MID_MRR);

  return [
    { label: "Top performers", count: top.length, threshold: "MRR > $50k" },
    { label: "Mid-tier", count: mid.length, threshold: "$10k–$50k" },
    { label: "Early-stage", count: early.length, threshold: "< $10k" },
  ];
}

const SEGMENT_COLORS = [
  "rgba(245, 158, 11, 0.7)",
  "rgba(148, 163, 184, 0.6)",
  "rgba(34, 197, 94, 0.5)",
];

export function CompetitionBreakdownChart({ startups }: Props) {
  const segments = segmentByMrr(startups);

  if (startups.length === 0) {
    return (
      <div className="chart-card">
        <h3 className="card-title">Competition breakdown</h3>
        <p className="card-desc">Startups segmented by revenue tier</p>
        <div className="chart-placeholder">No data to display</div>
      </div>
    );
  }

  const maxCount = Math.max(...segments.map((s) => s.count), 1);
  const data = segments.map((s) => ({
    name: s.label,
    count: s.count,
    threshold: s.threshold,
    pct: startups.length > 0 ? ((s.count / startups.length) * 100).toFixed(0) : "0",
  }));

  return (
    <div className="chart-card">
      <h3 className="card-title">Competition breakdown</h3>
      <p className="card-desc">Top: MRR &gt; $50k · Mid: $10k–$50k · Early: &lt; $10k</p>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ right: 20, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} horizontal={false} />
            <XAxis
              type="number"
              domain={[0, maxCount * 1.15]}
              tick={AXIS_STYLE}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={120}
              tick={AXIS_STYLE}
              tickLine={false}
            />
            <Tooltip
              {...TOOLTIP_STYLE}
              cursor={{ fill: "rgba(139, 92, 246, 0.1)" }}
              formatter={() => null}
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const p = payload[0].payload as (typeof data)[0];
                return (
                  <div
                    style={{
                      ...TOOLTIP_STYLE.contentStyle,
                      padding: "12px 14px",
                    }}
                  >
                    <div style={TOOLTIP_STYLE.labelStyle}>
                      {p.count} startups ({p.pct}%) · {p.threshold}
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={28}>
              {data.map((_, index) => (
                <Cell key={index} fill={SEGMENT_COLORS[index]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
